import type { QuoteSummaryLocation } from 'api/quote-marketplace';

export interface ParsedLocationData {
  totalPremium: number | null;
  coverageBreakdownHtml: string | null;
  discountsHtml: string | null;
}

interface LocationTable {
  addressKey: string;
  postalKey: string;
  html: string;
  premium: number | null;
}

// Finds a Canadian postal code anywhere in a header descriptor. Insurers vary
// in how they tack it on: some give a bare "L0R 2A0" as the last comma segment,
// others glue the province in ("ON L0R2A0") or append city/province as extra
// segments, so it cannot be pulled out positionally.
const POSTAL_SEARCH = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/;

/**
 * Reduce an address or postal code to a comparable key. The insurer writes
 * postal codes as "K2S 0L9" while the form may hold either "K2S 0L9" or
 * "K2S0L9", so spacing and case cannot be trusted on either side.
 */
function normalizeKey(value: string | null | undefined): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function extractPremiumFromHeader(headerText: string): number | null {
  const match = headerText.match(/\(Total \$?([\d,]+\.?\d*)\)/i);
  return match ? parseFloat(match[1].replace(/,/g, '')) : null;
}

/**
 * Split "Coverage Breakdown - Location 1: 406 Santa Anna Lane, K2S 0L9".
 * Returns null when the header is not a location table for `section`.
 *
 * The descriptor's shape differs by insurer -- "street, postal",
 * "street, city, PROV postal" -- so the postal is found by pattern anywhere in
 * the descriptor and the street is taken as the first comma segment rather than
 * by counting from the end.
 */
function parseLocationHeader(
  headerText: string,
  section: string,
): { addressKey: string; postalKey: string } | null {
  const match = headerText.match(/^(.+?)\s*-\s*Location\s*\d+\s*:\s*(.+)$/i);
  if (!match) return null;
  if (match[1].trim().toLowerCase() !== section.toLowerCase()) return null;

  const descriptor = match[2];
  const postalMatch = descriptor.toUpperCase().match(POSTAL_SEARCH);
  const postal = postalMatch ? postalMatch[0] : '';

  const parts = descriptor
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const address = parts.length ? parts[0] : '';

  return {
    addressKey: normalizeKey(address),
    postalKey: normalizeKey(postal),
  };
}

function parseTables(html: string | null, section: string): LocationTable[] {
  if (!html) return [];

  const container = document.createElement('div');
  container.innerHTML = html;

  const tables: LocationTable[] = [];
  container.querySelectorAll('table').forEach((table) => {
    const firstRow = table.querySelector('tr:first-child');
    if (!firstRow) return;

    const headerCell =
      firstRow.querySelector('td:first-child') ||
      firstRow.querySelector('th:first-child');
    if (!headerCell) return;

    const parsed = parseLocationHeader(headerCell.textContent || '', section);
    if (!parsed) return;

    tables.push({
      ...parsed,
      html: table.outerHTML,
      premium: extractPremiumFromHeader(firstRow.textContent || ''),
    });
  });

  return tables;
}

/**
 * Match `tables` to `locations`, returning an array parallel to `locations`.
 *
 * Acturis returns risk items in an order that does not track the order we sent
 * them in, and the insurer numbers its "Location N" tables from that same
 * order, so neither position nor the header number identifies a location.
 */
function matchTables(
  locations: QuoteSummaryLocation[],
  tables: LocationTable[],
): (LocationTable | null)[] {
  const matched: (LocationTable | null)[] = locations.map(() => null);
  const used = new Set<number>();

  const claim = (index: number, position: number) => {
    matched[index] = tables[position];
    used.add(position);
  };

  // Address and postal both agreeing is the only unambiguous match.
  locations.forEach((location, index) => {
    const addressKey = normalizeKey(location.address);
    const postalKey = normalizeKey(location.postal_code);
    if (!addressKey || !postalKey) return;

    const position = tables.findIndex(
      (table, at) =>
        !used.has(at) &&
        table.postalKey === postalKey &&
        table.addressKey === addressKey,
    );
    if (position !== -1) claim(index, position);
  });

  // The insurer may reformat an address ("Lane" -> "Ln"), so fall back to the
  // postal code alone where it still singles one table out.
  locations.forEach((location, index) => {
    if (matched[index]) return;

    const postalKey = normalizeKey(location.postal_code);
    if (!postalKey) return;

    const hits = tables
      .map((table, at) => ({ table, at }))
      .filter(
        ({ table, at }) => !used.has(at) && table.postalKey === postalKey,
      );
    if (hits.length === 1) claim(index, hits[0].at);
  });

  // Some insurers omit the postal code from the header entirely, giving only
  // the street/city/province (e.g. Portage: "10 Swayze Court, West Lincoln,
  // ON"). With no postal to match on, fall back to the address alone where it
  // still singles one table out.
  locations.forEach((location, index) => {
    if (matched[index]) return;

    const addressKey = normalizeKey(location.address);
    if (!addressKey) return;

    const hits = tables
      .map((table, at) => ({ table, at }))
      .filter(
        ({ table, at }) =>
          !used.has(at) &&
          !!table.addressKey &&
          table.addressKey === addressKey,
      );
    if (hits.length === 1) claim(index, hits[0].at);
  });

  return matched;
}

export function getLocationsParsedData(
  locations: QuoteSummaryLocation[],
  coverageHtml: string | null,
  discountsHtml: string | null,
): ParsedLocationData[] {
  const coverage = matchTables(
    locations,
    parseTables(coverageHtml, 'Coverage Breakdown'),
  );
  const discounts = matchTables(
    locations,
    parseTables(discountsHtml, 'Discounts'),
  );

  return locations.map((_, index) => ({
    totalPremium: coverage[index]?.premium ?? null,
    coverageBreakdownHtml: coverage[index]?.html ?? null,
    discountsHtml: discounts[index]?.html ?? null,
  }));
}
